import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useDebounce } from "@/hooks/useDebounce";
import { queryKeys } from "@/lib/query-keys";

const USERNAME_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/;
const MIN_LENGTH = 3;
const MAX_LENGTH = 30;

function validateUsernameFormat(username: string): string | null {
  const trimmed = username.toLowerCase().trim();
  if (!trimmed) return null;
  if (trimmed.length < MIN_LENGTH) return `At least ${MIN_LENGTH} characters`;
  if (trimmed.length > MAX_LENGTH) return `Under ${MAX_LENGTH} characters`;
  if (!USERNAME_REGEX.test(trimmed)) return "Lowercase letters, numbers, hyphens only";
  return null;
}

interface UsernameCheckResult {
  isAvailable: boolean | null;
  isChecking: boolean;
  error: string | null;
}

export function useUsernameCheck(
  username: string,
  currentUsername?: string | null,
): UsernameCheckResult {
  const debounced = useDebounce(username.toLowerCase().trim(), 400);

  // Inline format error takes priority — skip the network call entirely
  const formatError = debounced ? validateUsernameFormat(debounced) : null;

  // If the user hasn't changed their username, treat it as available without a fetch
  const isSameAsCurrent =
    !!currentUsername &&
    !!debounced &&
    debounced === currentUsername.toLowerCase();

  const query = useQuery({
    queryKey: queryKeys.user.usernameCheck(debounced),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("public_profiles")
        .select("id")
        .ilike("username", debounced)
        .limit(1);

      if (error) throw error;
      return data && data.length > 0 ? "taken" : "available";
    },
    enabled: !!debounced && !formatError && !isSameAsCurrent,
    staleTime: 30_000,
    gcTime: 2 * 60 * 1000,
    retry: false,
  });

  if (!debounced) {
    return { isAvailable: null, isChecking: false, error: null };
  }

  if (isSameAsCurrent) {
    return { isAvailable: true, isChecking: false, error: null };
  }

  if (formatError) {
    return { isAvailable: false, isChecking: false, error: formatError };
  }

  if (query.isFetching) {
    return { isAvailable: null, isChecking: true, error: null };
  }

  if (query.isError) {
    return { isAvailable: null, isChecking: false, error: "Failed to check username" };
  }

  if (query.data === "taken") {
    return { isAvailable: false, isChecking: false, error: "Username is already taken" };
  }

  if (query.data === "available") {
    return { isAvailable: true, isChecking: false, error: null };
  }

  return { isAvailable: null, isChecking: false, error: null };
}
